<div class="conversation-detail-container">
  @if (isLoading) {
    <div class="loading-container">
      <p-progressSpinner />
      <p i18n="@@convDetail.loading">Loading session...</p>
    </div>
  } @else if (session) {
    <!-- Header -->
    <div class="header">
      <div class="header-left">
        @if (isPublicMode) {
          <!-- Back-arrow только для публичного режима: у залогиненного
               админа есть штатная навигация (sidebar + breadcrumb), а вот
               владельцу share-ссылки надо явно вернуться на тестер. -->
          <a [routerLink]="agentRouterLink"
             [queryParamsHandling]="'preserve'"
             class="back-to-agent-link"
             i18n-pTooltip="@@convDetail.backToAgent"
             pTooltip="Back to Agent"
             tooltipPosition="right">
            <i class="pi pi-arrow-left"></i>
            <span i18n="@@convDetail.backToAgentLabel">Back to Agent</span>
          </a>
        }
        <h1 class="title">
          <ng-container i18n="@@convDetail.title">Conversation with</ng-container>
          <a [routerLink]="agentRouterLink" class="agent-link">
            {{ session.agent_title || unknownAgentLabel }}
          </a>
        </h1>
      </div>
      <div class="header-right">
        <span
          class="session-id"
          (click)="copySessionId()"
          i18n-pTooltip="@@convDetail.copyId"
          pTooltip="Copy Conversation ID"
          tooltipPosition="left">
          {{ sessionId }}
        </span>
      </div>
    </div>

    @if (isTestSession()) {
      <!-- Test session: показываем исход прогона. Аудио-плеер ниже больше
           не в @else: у e2e-теста запись есть, и слушать её надо так же,
           как у обычной сессии. Text-only симуляция записи не имеет —
           там плеер просто не появится, решает hasAudio().
           Меню всегда доступно — Copy session URL полезно и не-админам,
           synth-пункт (внутри synthOnlyMenuItems) сам гейтится по isAdmin. -->
      <div class="test-info-card">
        @if (synthOnlyMenuItems.length > 0) {
          <!-- Такая же схема, как в audio-controls (`.controls-right`
               ниже): normal flex row, p-menu popup с appendTo="body".
               Никаких position:absolute, никаких оверлеев внутри карточки
               — иначе PrimeNG рассчитывает координаты попапа от viewport
               и применяет их к absolute-родителю, попап улетает и всю
               страницу перекашивает. -->
          <div class="test-info-actions">
            <p-button
              icon="pi pi-ellipsis-v"
              [text]="true"
              (onClick)="synthMenu.toggle($event)" />
            <p-menu #synthMenu [model]="synthOnlyMenuItems" [popup]="true" appendTo="body" />
          </div>
        }
        <div class="test-info-row">
          <label i18n="@@convDetail.test.link">Test</label>
          <a [routerLink]="['/tests', session.test_id]" class="test-link">
            <i class="pi pi-external-link"></i>
            <span i18n="@@convDetail.test.open">Open test</span>
          </a>
        </div>

        <div class="test-info-row">
          <label i18n="@@convDetail.test.status">Status</label>
          <p-tag [value]="testStatusLabel(session.test_status)" [severity]="testStatusSeverity(session.test_status)"></p-tag>
        </div>

        @if (session.test_eval_result?.success != null) {
          <div class="test-info-row">
            <label i18n="@@convDetail.test.outcome">Outcome</label>
            @if (session.test_eval_result!.success) {
              <p-tag severity="success" i18n-value="@@convDetail.test.passed" value="Passed"></p-tag>
            } @else {
              <p-tag severity="danger" i18n-value="@@convDetail.test.failed" value="Failed"></p-tag>
            }
          </div>
        }

        @if (session.test_eval_result?.completion_reason) {
          <div class="test-info-row">
            <label i18n="@@convDetail.test.completionReason">Completion reason</label>
            <span>{{ evalCompletionReasonLabel(session.test_eval_result!.completion_reason) }}</span>
          </div>
        }

        <div class="test-info-row test-info-results">
          <label i18n="@@convDetail.test.result">Result</label>
          @if (session.test_eval_result?.reasons && session.test_eval_result!.reasons!.length > 0) {
            <ul class="test-results-list">
              @for (r of session.test_eval_result!.reasons; track $index) {
                <li>{{ r }}</li>
              }
            </ul>
          } @else {
            <span class="test-no-results" i18n="@@convDetail.test.noResult">No results yet</span>
          }
        </div>
      </div>
    }

    @if (hasAudio()) {
      <!-- Audio Player -->
      <div class="audio-container">
        <div id="waveform"></div>
      </div>

      <!-- Audio Controls -->
      <div class="audio-controls">
        <div class="controls-left">
          <p-button
            [label]="audioLoadError ? retryLabel : (isAudioLoading ? loadingLabel : (isPlaying ? pauseLabel : playLabel))"
            [icon]="audioLoadError ? 'pi pi-refresh' : (isAudioLoading ? 'pi pi-spin pi-spinner' : (isPlaying ? 'pi pi-pause' : 'pi pi-play'))"
            (onClick)="audioLoadError ? retryAudioLoad() : togglePlay()"
            [disabled]="isAudioLoading"
            [loading]="isAudioLoading"
            [severity]="audioLoadError ? 'warn' : 'primary'" />

          <p-button
            [label]="playbackRate + 'x'"
            (onClick)="changePlaybackRate()"
            severity="secondary"
            [outlined]="true"
            [disabled]="isAudioLoading || audioLoadError" />

          <p-button
            icon="pi pi-step-backward"
            (onClick)="skipBackward()"
            severity="secondary"
            [outlined]="true"
            [text]="true"
            [disabled]="isAudioLoading || audioLoadError"
            i18n-pTooltip="@@convDetail.rewind5"
            pTooltip="Rewind 5 seconds"
            tooltipPosition="top"
            i18n-ariaLabel="@@convDetail.rewind5"
            ariaLabel="Rewind 5 seconds" />

          <p-button
            icon="pi pi-step-forward"
            (onClick)="skipForward()"
            severity="secondary"
            [outlined]="true"
            [text]="true"
            [disabled]="isAudioLoading || audioLoadError"
            i18n-pTooltip="@@convDetail.forward10"
            pTooltip="Fast forward 10 seconds"
            tooltipPosition="top"
            i18n-ariaLabel="@@convDetail.forward10"
            ariaLabel="Fast forward 10 seconds" />
        </div>

        <div class="controls-right">
          <span class="time-display">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</span>

          <p-button
            icon="pi pi-ellipsis-v"
            [text]="true"
            (onClick)="menu.toggle($event)" />
          <p-menu #menu [model]="menuItems" [popup]="true" appendTo="body" />
        </div>
      </div>
    }

    <!-- Tabs -->
    <p-tabs value="0">
      <p-tablist>
        <p-tab value="0" i18n="@@convDetail.tab.overview">Overview</p-tab>
        <p-tab value="1" i18n="@@convDetail.tab.transcription">Transcription</p-tab>
        <p-tab value="3" [disabled]="!session.telecom_provider" i18n="@@convDetail.tab.phoneCall">Phone call</p-tab>
      </p-tablist>
      <p-tabpanels>
        <p-tabpanel value="0">
          <div class="tab-content">
            <div class="metadata-section">
              <h3 i18n="@@convDetail.summary">Summary</h3>
              <p i18n="@@convDetail.emptyConv">The conversation is empty.</p>
            </div>

            @if (session.error_message) {
              <p-message severity="error" [text]="session.error_message" styleClass="w-full session-error"></p-message>
            }

            <div class="metadata-grid">
              <div class="metadata-item">
                <label i18n="@@convDetail.callStatus">Call status</label>
                <div class="status-row">
                  <p-tag
                    [value]="sessionStatusLabel(session.status)"
                    [severity]="sessionStatusSeverity(session.status)">
                  </p-tag>
                  @if (session.status === 'Active') {
                    <app-live-listen-button
                      [agentId]="agentId"
                      [sessionId]="sessionId"
                      size="small" />
                  }
                </div>
              </div>

              <div class="metadata-item">
                <label i18n="@@convDetail.userId">User ID</label>
                <span i18n="@@convDetail.noUserId">No user ID</span>
              </div>

              <div class="metadata-item">
                <label i18n="@@convDetail.date">Date</label>
                <span>{{ session.created_at | date:'MMM d, yyyy h:mm a' }}</span>
              </div>

              <div class="metadata-item">
                <label i18n="@@convDetail.duration">Connection duration</label>
                <span>{{ formatTime(duration) }}</span>
              </div>

              @if (session.campaign_id) {
                <div class="metadata-item">
                  <label i18n="@@convDetail.campaign">Campaign</label>
                  <a [routerLink]="['/outbound-campaigns', session.campaign_id]"
                     class="campaign-link"
                     i18n-pTooltip="@@convDetail.campaign.goto"
                     pTooltip="Go to campaign"
                     tooltipPosition="top">
                    <i class="pi pi-external-link"></i> {{ session.campaign_name || campaignFallbackLabel }}
                  </a>
                </div>
              }
            </div>
          </div>
        </p-tabpanel>

        <p-tabpanel value="1">
          <div class="tab-content">
            @if (session.history && session.history.length > 0) {
              <app-chat-history-view
                [messages]="session.history"
                [assistantName]="session.agent_title || agentFallbackLabel"
                [assistantLinkFor]="agentRouterLinkFor"
                [clickable]="true"
                [testFromHere]="!isPublicMode"
                (messageClick)="seekToMessage($event)"
                (createTestFrom)="createTestFromMessage($event)">
              </app-chat-history-view>
            } @else {
              <p i18n="@@convDetail.noTranscript">No transcript available.</p>
            }
          </div>
        </p-tabpanel>

        <p-tabpanel value="3">
          <div class="tab-content">
            <div class="phone-call-info">
              <div class="phone-call-item">
                <label i18n="@@convDetail.callerNumber">Caller Number</label>
                <span>{{ session.from_number || '-' }}</span>
              </div>

              <div class="phone-call-item">
                <label i18n="@@convDetail.receiverNumber">Receiver Number</label>
                <span>{{ session.to_number || '-' }}@if (session.telecom_provider) {
                  <span class="provider"> ({{ session.telecom_provider }})</span>
                }</span>
              </div>

              <!-- Карточки звонков этого разговора. Обычно одна, но после
                   перевода на оператора их две: нога клиента и нога
                   оператора. В публичном режиме список не грузим — эндпоинт
                   под [Authorize], — поэтому блока просто нет. -->
              @if (calls().length > 0) {
                <div class="phone-call-item">
                  <label i18n="@@convDetail.callCards">Call details</label>
                  <span class="call-links">
                    @for (call of calls(); track call.id) {
                      <a [routerLink]="['/calls', call.id]" class="call-link">
                        <i class="pi pi-phone"></i>
                        <span>{{ call.to_phone_number || call.from_number || call.id }}</span>
                      </a>
                    }
                  </span>
                </div>
              }
            </div>
          </div>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  } @else {
    <div class="error-message">
      <p i18n="@@convDetail.notFound">Session not found.</p>
    </div>
  }
</div>

<!-- Synth drawer (admin only).
     appendTo="body" — обязательно. Дефолтный p-drawer'овский appendTo
     'self' оставляет контейнер и mask ВНУТРИ template'а, значит когда
     внутри открывается вложенный model-tester-drawer (inline тоже),
     оба mask'а/контейнера рендерятся в разных DOM-контекстах —
     autoZIndex не может выстроить их в предсказуемый стек, всё
     затемняется. Body-mount разводит их как full-screen overlay'и с
     чистой z-index иерархией. -->
@if (isAdmin) {
  <p-drawer
    [(visible)]="showSynthDrawer"
    position="right"
    styleClass="synth-drawer"
    [modal]="true"
    appendTo="body">
    <ng-template pTemplate="header">
      <div class="synth-drawer-header">
        <i class="pi pi-sparkles"></i>
        <h3 i18n="@@convDetail.synth.title">Generate synthetic audio</h3>
      </div>
    </ng-template>
    <div class="synth-drawer-body">
      <!-- Top actions row: Refresh (всегда) + Download (когда есть аудио).
           Refresh перезагружает сессию с бэка и пере-сидирует все поля
           drawer'а — юзер жмёт осознанно если хочет откатить локальные
           правки или подтянуть свежие данные (напр. после MCP-мутации). -->
      <div class="synth-actions-row">
        <p-button
          icon="pi pi-refresh"
          severity="secondary"
          [outlined]="true"
          [loading]="isReloadingSynth"
          [disabled]="isReloadingSynth || synthInProgress"
          (onClick)="reloadSynth()"
          i18n-label="@@convDetail.synth.refresh"
          label="Refresh">
        </p-button>
        @if (session?.synthesized_audio_url) {
          <p-button
            icon="pi pi-download"
            severity="secondary"
            [outlined]="true"
            [disabled]="synthInProgress"
            (onClick)="downloadSynth()"
            i18n-label="@@convDetail.synth.download"
            label="Download synthesized audio">
          </p-button>
        }
      </div>
      <!-- Спиннер пока идёт синтез. Основной контент оставляем видимым
           (юзер может параллельно доработать историю / поменять голос
           для следующего Regenerate). -->
      @if (synthInProgress) {
        <div class="synth-in-progress-row">
          <p-progressSpinner styleClass="synth-in-progress-spinner" strokeWidth="4"></p-progressSpinner>
          <span i18n="@@convDetail.synth.inProgress">Synthesizing… you can leave the drawer open, we'll update it when the audio is ready.</span>
        </div>
      }
      <p class="synth-drawer-description" i18n="@@convDetail.synth.description">
        Pick a TTS model and voice — the backend will re-render the user side of this
        session with the selected voice and store the audio as a separate track.
      </p>
      <div class="synth-field">
        <label class="synth-field-label" i18n="@@convDetail.synth.ttsLabel">TTS Model</label>
        <app-model-selector-button
          [modelTypes]="['tts']"
          [modelsHierarchy]="modelsHierarchy"
          [selectedVersionId]="synthTtsVersionId"
          [currentVoiceId]="synthTtsVoiceId"
          [currentAdditionalProperties]="getSynthPropertiesDict()"
          [allowSelectFromTester]="true"
          [includeAll]="modelsIncludeAll"
          (includeAllChange)="onModelsIncludeAllChange($event)"
          [label]="selectTtsModelLabel"
          (modelSelected)="onSynthModelSelected($event)"
          (testerSelected)="onSynthTesterSelected($event)">
        </app-model-selector-button>
      </div>
      <div class="synth-field">
        <label class="synth-field-label" i18n="@@convDetail.synth.historyLabel">Chat history</label>
        <p class="synth-field-hint" i18n="@@convDetail.synth.historyHint">
          The backend will use these messages when rendering the user side. Edit,
          add, or remove entries to shape what gets synthesized.
        </p>
        <app-chat-history-view
          [messages]="synthChatHistory"
          [editable]="true"
          [showMetrics]="false"
          [showTimestamps]="false"
          (messagesChange)="synthChatHistory = $event">
        </app-chat-history-view>
      </div>
    </div>
    <ng-template pTemplate="footer">
      <div class="synth-drawer-footer">
        <p-button
          severity="secondary"
          [outlined]="true"
          (onClick)="closeSynthDrawer()"
          i18n-label="@@common.cancel"
          label="Cancel">
        </p-button>
        <p-button
          icon="pi pi-sparkles"
          (onClick)="generateSyntheticAudio()"
          [loading]="synthInProgress"
          [disabled]="!synthTtsVersionId || synthInProgress"
          i18n-label="@@convDetail.synth.generate"
          label="Generate">
        </p-button>
      </div>
    </ng-template>
  </p-drawer>
}

